/**
 * adminGroupCoursesApi.js - Sprint 1
 * 
 * Servicio para comunicación con API de GroupCourse Builder (backend)
 * @see PLAN_GROUPCOURSE_BUILDER.md
 * 
 * Endpoints:
 * - listGroupCourses() - GET /api/admin/group-courses
 * - createGroupCourse() - POST /api/admin/group-courses
 * - getGroupCourse() - GET /api/admin/group-courses/:courseId
 * - updateGroupCourse() - PUT /api/admin/group-courses/:courseId
 * - deleteGroupCourse() - DELETE /api/admin/group-courses/:courseId
 * - updateWeek() - PUT /api/admin/group-courses/:courseId/week/:weekNumber
 * - duplicateWeek() - POST /api/admin/group-courses/:courseId/week/:weekNumber/duplicate
 * - validateCourse() - POST /api/admin/group-courses/:courseId/validate
 * - publishCourse() - POST /api/admin/group-courses/:courseId/publish
 */

import apiClient from './apiClient';

/**
 * Listar todos los cursos grupales
 * @param {Object} filters - { status: 'draft' | 'published' | 'archived' }
 * @returns {Promise<{courses: Array, total: number}>}
 */
export const listGroupCourses = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) {
      params.append('status', filters.status);
    }
    
    const response = await apiClient.get(`/admin/group-courses?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error listando cursos grupales:', error);
    throw error;
  }
};

/**
 * Crear nuevo curso grupal
 * @param {Object} courseData - { courseId, title, shortDescription, estimatedHours, skills, groupCourseConfig }
 * @returns {Promise<Object>} - Curso creado
 */
export const createGroupCourse = async (courseData) => {
  try {
    const response = await apiClient.post('/admin/group-courses', courseData);
    return response.data;
  } catch (error) {
    console.error('Error creando curso grupal:', error);
    throw error;
  }
};

/**
 * Obtener curso grupal específico
 * @param {string} courseId - ID del curso
 * @returns {Promise<Object>} - { course, stats }
 */
export const getGroupCourse = async (courseId) => {
  try {
    const response = await apiClient.get(`/admin/group-courses/${courseId}`);
    return response.data;
  } catch (error) {
    console.error('Error obteniendo curso grupal:', error);
    throw error;
  }
};

/**
 * Actualizar curso grupal (metadata y configuración)
 * @param {string} courseId - ID del curso
 * @param {Object} updates - Campos a actualizar
 * @returns {Promise<Object>} - Curso actualizado
 */
export const updateGroupCourse = async (courseId, updates) => {
  try {
    const response = await apiClient.put(`/admin/group-courses/${courseId}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error actualizando curso grupal:', error);
    throw error;
  }
};

/**
 * Eliminar curso grupal (solo drafts)
 * @param {string} courseId - ID del curso
 * @returns {Promise<Object>} - Confirmación
 */
export const deleteGroupCourse = async (courseId) => {
  try {
    const response = await apiClient.delete(`/admin/group-courses/${courseId}`);
    return response.data;
  } catch (error) {
    console.error('Error eliminando curso grupal:', error);
    throw error;
  }
};

/**
 * Actualizar semana específica
 * @param {string} courseId - ID del curso
 * @param {number} weekNumber - Número de semana (1-18)
 * @param {Object} weekData - { b1_resources, b2_execution_template, b3_criteria, b4_coach_config, b5_formalization_template, b6_criteria }
 * @returns {Promise<Object>} - Semana actualizada
 */
export const updateWeek = async (courseId, weekNumber, weekData) => {
  try {
    const response = await apiClient.put(
      `/admin/group-courses/${courseId}/week/${weekNumber}`,
      weekData
    );
    return response.data;
  } catch (error) {
    console.error('Error actualizando semana:', error);
    throw error;
  }
};

/**
 * Duplicar semana a otro número
 * @param {string} courseId - ID del curso
 * @param {number} sourceWeekNumber - Semana origen
 * @param {number} targetWeekNumber - Semana destino
 * @returns {Promise<Object>} - Semana duplicada
 */
export const duplicateWeek = async (courseId, sourceWeekNumber, targetWeekNumber) => {
  try {
    const response = await apiClient.post(
      `/admin/group-courses/${courseId}/week/${sourceWeekNumber}/duplicate`,
      { targetWeekNumber }
    );
    return response.data;
  } catch (error) {
    console.error('Error duplicando semana:', error);
    throw error;
  }
};

/**
 * Validar curso antes de publicar
 * @param {string} courseId - ID del curso
 * @returns {Promise<Object>} - { valid, errors[], warnings[], summary }
 */
export const validateCourse = async (courseId) => {
  try {
    const response = await apiClient.post(`/admin/group-courses/${courseId}/validate`);
    return response.data;
  } catch (error) {
    console.error('Error validando curso:', error);
    throw error;
  }
};

/**
 * Publicar curso grupal (requiere validación exitosa)
 * @param {string} courseId - ID del curso
 * @returns {Promise<Object>} - Curso publicado
 */
export const publishCourse = async (courseId) => {
  try {
    const response = await apiClient.post(`/admin/group-courses/${courseId}/publish`);
    return response.data;
  } catch (error) {
    console.error('Error publicando curso:', error);
    throw error;
  }
};

/**
 * Ocultar curso publicado del catálogo
 * @param {string} courseId - ID del curso
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const hideCourse = async (courseId) => {
  try {
    const response = await apiClient.post(`/admin/group-courses/${courseId}/hide`);
    return response.data;
  } catch (error) {
    console.error('Error ocultando curso:', error);
    throw error;
  }
};

/**
 * Mostrar curso publicado en catálogo
 * @param {string} courseId - ID del curso
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const showCourse = async (courseId) => {
  try {
    const response = await apiClient.post(`/admin/group-courses/${courseId}/show`);
    return response.data;
  } catch (error) {
    console.error('Error mostrando curso:', error);
    throw error;
  }
};

/**
 * Despublicar curso (solo si no hay inscripciones)
 * @param {string} courseId - ID del curso
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const unpublishCourse = async (courseId) => {
  try {
    const response = await apiClient.post(`/admin/group-courses/${courseId}/unpublish`);
    return response.data;
  } catch (error) {
    console.error('Error despublicando curso:', error);
    throw error;
  }
};

/**
 * Archivar curso
 * @param {string} courseId - ID del curso
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const archiveCourse = async (courseId) => {
  try {
    const response = await apiClient.post(`/admin/group-courses/${courseId}/archive`);
    return response.data;
  } catch (error) {
    console.error('Error archivando curso:', error);
    throw error;
  }
};

/**
 * Restaurar curso archivado
 * @param {string} courseId - ID del curso
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const restoreCourse = async (courseId) => {
  try {
    const response = await apiClient.post(`/admin/group-courses/${courseId}/restore`);
    return response.data;
  } catch (error) {
    console.error('Error restaurando curso:', error);
    throw error;
  }
};

export default {
  listGroupCourses,
  createGroupCourse,
  getGroupCourse,
  updateGroupCourse,
  deleteGroupCourse,
  updateWeek,
  duplicateWeek,
  validateCourse,
  publishCourse,
  hideCourse,
  showCourse,
  unpublishCourse,
  archiveCourse,
  restoreCourse
};
